import { supabase } from "./supabaseClient";

const NOTICE_ATTACHMENTS_BUCKET = "notice-attachments";
const IMAGE_MIME_RE = /^image\//i;
const IMAGE_EXT_RE = /\.(jpe?g|png|gif|webp|bmp|heic|heif|svg)$/i;

const NOTICE_SELECT =
  "id, title, body, notice_type, course_type_code, course_run_id, full_cert_type_code, full_cert_run_id, suppress_parent_notices, excluded_run_ids, show_on_top, attachments, is_published, published_at, starts_at, expires_at, sort_order, created_at";

/** @typedef {{ path: string, name: string, mime_type?: string, size?: number, uploaded_at?: string }} LmsNoticeAttachment */

export function normalizeNoticeAttachments(value) {
  if (!Array.isArray(value)) return [];
  const out = [];
  for (const row of value) {
    if (!row || typeof row !== "object") continue;
    const path = typeof row.path === "string" ? row.path.trim() : "";
    if (!path) continue;
    out.push({
      path,
      name:
        typeof row.name === "string" && row.name.trim()
          ? row.name.trim()
          : path.split("/").pop() || "file",
      mime_type: typeof row.mime_type === "string" ? row.mime_type : "",
      size: typeof row.size === "number" && Number.isFinite(row.size) ? row.size : 0,
      uploaded_at: typeof row.uploaded_at === "string" ? row.uploaded_at : "",
    });
  }
  return out;
}

export function isNoticeImageAttachment(fileOrMeta) {
  if (!fileOrMeta) return false;
  if (typeof fileOrMeta === "string") return IMAGE_EXT_RE.test(fileOrMeta);
  const mime = fileOrMeta.mime_type || fileOrMeta.type || "";
  if (IMAGE_MIME_RE.test(mime)) return true;
  return IMAGE_EXT_RE.test(fileOrMeta.name || "");
}

export function getNoticeAttachmentUrl(path) {
  if (!path) return "";
  return supabase.storage.from(NOTICE_ATTACHMENTS_BUCKET).getPublicUrl(path).data.publicUrl;
}

export function formatNoticeFileSize(bytes) {
  if (!Number.isFinite(bytes) || bytes <= 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

/** Type-level notices may hide specific runs via excluded_run_ids (TMS chip unselect). */
function isExcludedFromRun(notice, runId) {
  if (!runId) return false;
  const excluded = Array.isArray(notice?.excluded_run_ids) ? notice.excluded_run_ids : [];
  return excluded.includes(runId);
}

/** Notice is visible now: published, started (or no start), not expired. */
export function isNoticeActiveNow(notice, now = new Date()) {
  if (!notice?.is_published) return false;
  if (notice.starts_at && new Date(notice.starts_at) > now) return false;
  if (notice.expires_at && new Date(notice.expires_at) <= now) return false;
  return true;
}

function sortNotices(a, b) {
  // Newest first (published → starts → created)
  const aDate = a.published_at || a.starts_at || a.created_at || "";
  const bDate = b.published_at || b.starts_at || b.created_at || "";
  return String(bDate).localeCompare(String(aDate));
}

/**
 * Merge type-level + run-level notices per enrollment scope.
 * If any run-level notice has suppress_parent_notices, hide type-level for that scope.
 * Type-level notices listing a run in excluded_run_ids are also hidden for that run.
 *
 * @param {object[]} rows
 * @param {{ kind: 'course'|'full_cert', typeCode: string, runId: string }[]} scopes
 */
export function resolveNoticesForScopes(rows, scopes = []) {
  const active = (rows || []).filter((n) => isNoticeActiveNow(n));
  const visible = new Map();

  for (const scope of scopes) {
    if (!scope?.runId && !scope?.typeCode) continue;

    const typeRows = active.filter((n) => {
      if (isExcludedFromRun(n, scope.runId)) return false;
      if (scope.kind === "course") {
        return (
          n.course_type_code === scope.typeCode &&
          !n.course_run_id &&
          !n.full_cert_type_code &&
          !n.full_cert_run_id
        );
      }
      return (
        n.full_cert_type_code === scope.typeCode &&
        !n.full_cert_run_id &&
        !n.course_type_code &&
        !n.course_run_id
      );
    });

    const runRows = active.filter((n) => {
      if (scope.kind === "course") return n.course_run_id === scope.runId;
      return n.full_cert_run_id === scope.runId;
    });

    const suppress = runRows.some((n) => n.suppress_parent_notices);
    const forScope = suppress ? runRows : [...typeRows, ...runRows];
    forScope.forEach((n) => visible.set(n.id, n));
  }

  return Array.from(visible.values()).sort(sortNotices);
}

async function fetchPublishedNotices(orFilter) {
  if (!orFilter) return [];

  const nowIso = new Date().toISOString();
  const { data, error } = await supabase
    .from("lms_notices")
    .select(NOTICE_SELECT)
    .eq("is_published", true)
    .or(`starts_at.is.null,starts_at.lte.${nowIso}`)
    .or(`expires_at.is.null,expires_at.gt.${nowIso}`)
    .or(orFilter)
    .order("sort_order", { ascending: true })
    .order("published_at", { ascending: false });

  if (error) throw error;
  return data || [];
}

/** Dashboard: full-cert type + run notices for the student's enrollments. */
export function buildFullCertScopes(certifications = []) {
  return certifications
    .filter((c) => c?.id)
    .map((c) => ({
      kind: "full_cert",
      runId: c.id,
      typeCode: c.full_certification?.code || null,
    }));
}

export async function fetchNoticesForFullCertEnrollments(certifications = []) {
  const scopes = buildFullCertScopes(certifications);
  const typeCodes = [...new Set(scopes.map((s) => s.typeCode).filter(Boolean))];
  const runIds = [...new Set(scopes.map((s) => s.runId).filter(Boolean))];
  if (typeCodes.length === 0 && runIds.length === 0) return [];

  const parts = [];
  if (typeCodes.length) parts.push(`full_cert_type_code.in.(${typeCodes.join(",")})`);
  if (runIds.length) parts.push(`full_cert_run_id.in.(${runIds.join(",")})`);

  const rows = await fetchPublishedNotices(parts.join(","));
  return resolveNoticesForScopes(rows, scopes);
}

/** Course page: course-type + course-run notices for one module. */
export async function fetchNoticesForCourseRun({ courseTypeCode, courseRunId } = {}) {
  if (!courseTypeCode && !courseRunId) return [];

  const parts = [];
  if (courseTypeCode) parts.push(`course_type_code.eq.${courseTypeCode}`);
  if (courseRunId) parts.push(`course_run_id.eq.${courseRunId}`);

  const rows = await fetchPublishedNotices(parts.join(","));
  return resolveNoticesForScopes(rows, [
    { kind: "course", typeCode: courseTypeCode || null, runId: courseRunId || null },
  ]);
}

/** College-wide: all scope FKs null — Notices tab (all published college notices). */
export async function fetchCollegeNotices() {
  const nowIso = new Date().toISOString();
  const { data, error } = await supabase
    .from("lms_notices")
    .select(NOTICE_SELECT)
    .eq("is_published", true)
    .is("course_type_code", null)
    .is("course_run_id", null)
    .is("full_cert_type_code", null)
    .is("full_cert_run_id", null)
    .or(`starts_at.is.null,starts_at.lte.${nowIso}`)
    .or(`expires_at.is.null,expires_at.gt.${nowIso}`)
    .order("published_at", { ascending: false, nullsFirst: false })
    .order("created_at", { ascending: false });

  if (error) throw error;
  return (data || []).filter((n) => isNoticeActiveNow(n)).sort(sortNotices);
}

/**
 * Dashboard top banners: every active notice with show_on_top for this student
 * (college + programme/intake + course module notices for their enrollments).
 *
 * @param {Array} certifications
 * @param {{ courseTypeCode: string, courseRunId: string }[]} [courseScopes]
 */
export async function fetchDashboardTopNotices(certifications = [], courseScopes = []) {
  const nowIso = new Date().toISOString();
  const certScopes = buildFullCertScopes(certifications);
  const typeCodes = [...new Set(certScopes.map((s) => s.typeCode).filter(Boolean))];
  const runIds = [...new Set(certScopes.map((s) => s.runId).filter(Boolean))];
  const courseTypeCodes = [
    ...new Set(courseScopes.map((s) => s.courseTypeCode).filter(Boolean)),
  ];
  const courseRunIds = [...new Set(courseScopes.map((s) => s.courseRunId).filter(Boolean))];

  const parts = [
    "and(course_type_code.is.null,course_run_id.is.null,full_cert_type_code.is.null,full_cert_run_id.is.null)",
  ];
  if (typeCodes.length) parts.push(`full_cert_type_code.in.(${typeCodes.join(",")})`);
  if (runIds.length) parts.push(`full_cert_run_id.in.(${runIds.join(",")})`);
  if (courseTypeCodes.length) parts.push(`course_type_code.in.(${courseTypeCodes.join(",")})`);
  if (courseRunIds.length) parts.push(`course_run_id.in.(${courseRunIds.join(",")})`);

  const { data, error } = await supabase
    .from("lms_notices")
    .select(NOTICE_SELECT)
    .eq("is_published", true)
    .eq("show_on_top", true)
    .or(`starts_at.is.null,starts_at.lte.${nowIso}`)
    .or(`expires_at.is.null,expires_at.gt.${nowIso}`)
    .or(parts.join(","))
    .order("published_at", { ascending: false, nullsFirst: false })
    .order("created_at", { ascending: false });

  if (error) throw error;

  const active = (data || []).filter((n) => isNoticeActiveNow(n));
  const college = active.filter(
    (n) =>
      !n.course_type_code &&
      !n.course_run_id &&
      !n.full_cert_type_code &&
      !n.full_cert_run_id
  );
  const fullCertScoped = resolveNoticesForScopes(active, certScopes);
  const courseResolveScopes = courseScopes
    .filter((s) => s?.courseRunId || s?.courseTypeCode)
    .map((s) => ({
      kind: "course",
      typeCode: s.courseTypeCode || null,
      runId: s.courseRunId || null,
    }));
  const courseScoped = resolveNoticesForScopes(active, courseResolveScopes);
  const byId = new Map();
  [...college, ...fullCertScoped, ...courseScoped].forEach((n) => byId.set(n.id, n));
  return Array.from(byId.values()).sort(sortNotices);
}

/** Strip to plain text (dashboard teaser / fallback). */
export function stripHtmlToPlain(html) {
  return String(html || "")
    .replace(/<[^>]*>/g, " ")
    .replace(/\s+/g, " ")
    .trim();
}

const NOTICE_HTML_ALLOWED = new Set(["P", "BR", "STRONG", "B", "EM", "I", "UL", "OL", "LI", "A"]);

/** Turn Quill bare links (www.google.com) into safe absolute hrefs. */
function normalizeNoticeHref(href) {
  const h = String(href || "").trim();
  if (!h) return null;
  if (/^(https?:|mailto:)/i.test(h)) return h;
  if (/^\/\//.test(h)) return `https:${h}`;
  // Quill often saves links without a protocol
  if (/^(www\.|[a-z0-9-]+(\.[a-z0-9-]+)+)/i.test(h)) return `https://${h}`;
  return null;
}

/**
 * Allow only bold/italic/lists/links from TMS Quill HTML.
 * Runs in browser (DOMParser). Falls back to plain text if unavailable.
 */
export function sanitizeNoticeHtml(html) {
  if (!html) return "";
  if (typeof DOMParser === "undefined") return stripHtmlToPlain(html);

  const doc = new DOMParser().parseFromString(String(html), "text/html");
  const body = doc.body;

  let guard = 0;
  while (guard++ < 50) {
    const bad = [...body.querySelectorAll("*")].find(
      (el) => !NOTICE_HTML_ALLOWED.has(el.tagName)
    );
    if (!bad) break;
    bad.replaceWith(...bad.childNodes);
  }

  body.querySelectorAll("*").forEach((el) => {
    if (el.tagName === "A") {
      const rawHref = el.getAttribute("href") || "";
      [...el.attributes].forEach((attr) => el.removeAttribute(attr.name));
      const href = normalizeNoticeHref(rawHref);
      if (href) {
        el.setAttribute("href", href);
        el.setAttribute("target", "_blank");
        el.setAttribute("rel", "noopener noreferrer");
      }
    } else {
      [...el.attributes].forEach((attr) => el.removeAttribute(attr.name));
    }
  });

  return body.innerHTML;
}

/**
 * Card colours aligned with TMS preview:
 * Info=blue, Reminder=amber, Notice=red, College=orange
 */
export const NOTICE_CARD_STYLES = {
  info: {
    border: "blue.100",
    bg: "blue.50",
    iconColor: "blue.500",
    badgeBg: "blue.100",
    badgeColor: "blue.800",
    label: "Info",
  },
  warning: {
    border: "yellow.100",
    bg: "yellow.50",
    iconColor: "yellow.600",
    badgeBg: "yellow.100",
    badgeColor: "yellow.900",
    label: "Reminder",
  },
  alert: {
    border: "red.100",
    bg: "red.50",
    iconColor: "red.500",
    badgeBg: "red.100",
    badgeColor: "red.800",
    label: "Notice",
  },
  college: {
    border: "orange.100",
    bg: "orange.50",
    iconColor: "orange.500",
    badgeBg: "orange.100",
    badgeColor: "orange.800",
    label: "College",
  },
};

export function getNoticeCardStyle(noticeType, { college = false } = {}) {
  if (college) return NOTICE_CARD_STYLES.college;
  return NOTICE_CARD_STYLES[noticeType] || NOTICE_CARD_STYLES.info;
}
